import { ReadonlyURLSearchParams } from "next/navigation";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";
import { APP_ENVIRONMENT } from "@/constants";
import { SierraAPIConfig } from "../sierra-web/types";
import { getTranslations } from "./get-translations";
import { onAgentMessageEnd } from "./on-agent-message-end";
import { onOpen } from "./on-open";
import { onSecretExpiry } from "./on-secret-expiry";

type GetSierraConfigParams = {
  auth: ReturnType<typeof useAuth>;
  store: ReturnType<typeof useStore>;
  searchParams: ReadonlyURLSearchParams | null;
};

export const getSierraConfig = async ({
  auth,
  store: { countryData, shop },
  searchParams,
}: GetSierraConfigParams): Promise<SierraAPIConfig> => {
  const customSierraVar = getCustomSierraVariables(searchParams);

  const isAuthenticated = auth.status === "authenticated";

  if (!auth.authClient) {
    throw new Error("Auth client not initialized");
  }

  const accessToken = isAuthenticated
    ? ((await auth.authClient.getAccessToken()) ?? "")
    : "";

  const idTokenClaims = isAuthenticated
    ? auth.authClient.getIdTokenClaims()
    : undefined;

  const { customerId = "", firstName, lastName } = idTokenClaims ?? {};

  const sierraConfig: SierraAPIConfig = {
    display: "custom",
    customContainer: document.getElementById("sierra-chat-container")!,
    customGreeting: getTranslations("cchp_ai_chat_greeting_message").replace(
      "%{firstname}",
      `${firstName}`,
    ),
    persistence: "tab",
    disableAutoFocus: true,
    canEndConversation: true,
    chatInterfaceStrings: {
      name: getTranslations("cchp_ai_chat_header"),
      printTranscriptMenuLabel: getTranslations(
        "cchp_ai_chat_print_transcript_button",
      ),
      disclosure: getTranslations("cchp_ai_chat_disclosure"),
      endConversationMenuLabel: getTranslations("cchp_ai_chat_end_chat_button"),
      errorMessage: getTranslations("cchp_ai_chat_error_message"),
      conversationEndedMessage: getTranslations("cchp_ai_chat_end_message"),
      agentTransferWaitingMessage: getTranslations(
        "cchp_ai_chat_agent_transfer_waiting_message",
      ),
      agentTransferQueueNextMessage: getTranslations(
        "cchp_ai_chat_agent_transfer_queue_next_message",
      ),
      agentTransferQueueSizeMessage: getTranslations(
        "cchp_ai_chat_agent_transfer_queue_size_message",
      ),
      agentJoinedMessage: getTranslations(
        "cchp_ai_chat_agent_transfer_join_message",
      ),
      agentLeftMessage: getTranslations("cchp_ai_chat_agent_left_message"),
      newChatButtonLabel: getTranslations("cchp_ai_chat_start_new_chat"),
      inputPlaceholder: getTranslations("cchp_ai_chat_message_placeholder"),
    },
    canPrintTranscript: true,
    canStartNewChat: true,
    unstyled: true,
    variables: {
      shop: shop,
      country: countryData.countryCode,
      language: countryData.defaultLanguage,
      storeId: "TS-GB",
      environment: APP_ENVIRONMENT === "prod" ? "production" : "staging",
      isAuthenticated: isAuthenticated as unknown as string,
      customerId,
      ...(isAuthenticated ? { firstName, lastName } : {}),
      ...customSierraVar,
    },
    secrets: {
      accessToken,
    },
    logoSVG:
      '<svg width="499" height="72" viewBox="0 0 499 72" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M459.939 15.1171H476.248C481.021 15.1171 483.708 17.6026 483.708 22.0781C483.708 26.5536 481.023 29.039 476.248 29.039H459.939V15.1171ZM459.939 70.8077V42.961H474.757C490.072 42.961 498.424 35.6026 498.424 22.0781C498.424 8.5536 490.07 1.1952 474.757 1.1952H445.219V70.8077H459.939ZM396.593 58.0781C385.753 58.0781 377.401 50.2214 377.401 37.3939V34.609C377.401 21.7814 385.756 13.9248 396.593 13.9248C407.434 13.9248 415.786 21.7814 415.786 34.609V37.3939C415.786 50.2214 407.434 58.0781 396.593 58.0781ZM396.593 72C415.19 72 430.505 58.5734 430.505 36.9936V35.0035C430.505 13.4266 415.19 0 396.593 0C377.997 0 362.681 13.4266 362.681 35.0064V36.9965C362.681 58.5734 377.997 72 396.593 72ZM333.245 29.039H302.616V1.19232H287.899V70.8048H302.616V42.9581H333.245V70.8048H347.962V1.19232H333.245V29.039ZM244.935 72C261.841 72 273.18 63.6451 273.18 50.1206C273.18 36.0979 264.229 31.225 249.71 29.8339C234.694 28.4429 231.811 26.352 231.811 20.9837C231.811 16.5082 236.583 13.5245 244.342 13.5245C252.297 13.5245 256.772 17.5018 257.57 22.3747H272.189C271.29 8.95104 261.346 0 244.339 0C228.427 0 217.29 8.25408 217.29 20.8829C217.29 34.3094 225.743 39.9773 240.362 41.567C253.489 42.9581 258.463 44.4499 258.463 50.1206C258.463 55.2931 253.789 58.0752 244.938 58.0752C236.186 58.0752 231.414 53.7984 230.916 48.6288H215.6C216.493 62.7523 227.036 72 244.935 72ZM164.684 15.1171H180.994C185.766 15.1171 188.453 17.6026 188.453 22.0781C188.453 26.5536 185.769 29.039 180.994 29.039H164.684V15.1171ZM164.684 70.8077V42.961H179.502C194.818 42.961 203.17 35.6026 203.17 22.0781C203.17 8.5536 194.815 1.1952 179.502 1.1952H149.965V70.8077H164.684ZM101.336 58.0781C90.4954 58.0781 82.1434 50.2214 82.1434 37.3939V34.609C82.1434 21.7814 90.4983 13.9248 101.336 13.9248C112.176 13.9248 120.528 21.7814 120.528 34.609V37.3939C120.531 50.2214 112.176 58.0781 101.336 58.0781ZM101.336 72C119.932 72 135.248 58.5734 135.248 36.9936V35.0035C135.248 13.4266 119.932 0 101.336 0C82.7395 0 67.4237 13.4266 67.4237 35.0064V36.9965C67.4237 58.5734 82.7395 72 101.336 72ZM58.0781 1.19232H0V15.1142H21.6806V70.8048H36.3974V15.1171H58.0781V1.19232Z"/></svg>',
    onSecretExpiry: (secretName: string) => {
      return onSecretExpiry({ auth, secretName });
    },
    onOpen: ({ isNewConversation }) => {
      onOpen({
        isNewConversation,
        userContext: {
          accessToken,
          customerId,
          isAuthenticated,
          firstName,
          lastName,
        },
      });
    },
    onAgentMessageEnd: () => {
      onAgentMessageEnd();
    },
  };

  return sierraConfig;
};

/**
 * Get the sierra-var query parameter to extract multiple key:value pairs
 * Keys must be valid JSON property names (starting with letter or underscore, followed by letters, numbers, or underscores)
 * Values must contain only alphanumeric characters, underscores (_), and hyphens (-)
 * @returns {Object} Parsed key-value pairs
 */
const getCustomSierraVariables = (
  searchParams: ReadonlyURLSearchParams | null,
): Record<string, string> => {
  const sierraRawVar = searchParams?.get("sierra-var"); // example: ?sierra-var=key1:value1,key2:value2,key3:value3
  const customSierraVar: Record<string, string> = {};
  if (!sierraRawVar) {
    return customSierraVar;
  }

  const pairs = sierraRawVar.split(",");
  const validKeyRegex = /^[a-zA-Z_][a-zA-Z0-9_]*$/; // Valid JSON property name (no $ sign)
  const validValueRegex = /^[a-zA-Z0-9_-]+$/; // Alphanumeric with _ and -

  pairs.forEach((pair) => {
    const trimmedPair = pair.trim();
    if (trimmedPair.includes(":")) {
      const colonIndex = trimmedPair.indexOf(":");
      const key = trimmedPair.substring(0, colonIndex).trim();
      const value = trimmedPair.substring(colonIndex + 1).trim();

      // Validate key is a valid JSON property name and value contains only allowed characters
      if (key && validKeyRegex.test(key) && validValueRegex.test(value)) {
        customSierraVar[key] = value;
      }
    }
  });

  return customSierraVar;
};
